/**
 * 8D Module API - Suppliers
 *
 * Database operations for supplier management.
 * Uses mod_8d schema for module isolation.
 */

import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// Types
// =============================================================================

export interface Supplier {
  id: string;
  tenant_id: string;
  factory_id: string;
  name: string;
  code: string;
  contact_email: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateSupplierInput {
  tenant_id: string;
  factory_id: string;
  name: string;
  code: string;
  contact_email?: string | null;
  is_active?: boolean;
}

export interface UpdateSupplierInput {
  name?: string;
  code?: string;
  contact_email?: string | null;
  is_active?: boolean;
}

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetches all suppliers for a factory.
 */
export async function fetchSuppliersByFactory(factoryId: string): Promise<Supplier[]> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('suppliers')
    .select('*')
    .eq('factory_id', factoryId)
    .order('name');

  if (error) throw error;
  return data;
}

/**
 * Fetches active suppliers for a factory (for dropdown selection).
 */
export async function fetchActiveSuppliers(factoryId: string): Promise<Supplier[]> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('suppliers')
    .select('*')
    .eq('factory_id', factoryId)
    .eq('is_active', true)
    .order('name');

  if (error) throw error;
  return data;
}

/**
 * Creates a new supplier.
 */
export async function createSupplier(input: CreateSupplierInput): Promise<Supplier> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('suppliers')
    .insert({
      tenant_id: input.tenant_id,
      factory_id: input.factory_id,
      name: input.name,
      code: input.code,
      contact_email: input.contact_email || null,
      is_active: input.is_active ?? true,
    })
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Updates a supplier.
 */
export async function updateSupplier(supplierId: string, updates: UpdateSupplierInput): Promise<void> {
  const { error } = await supabase
    .schema('mod_8d')
    .from('suppliers')
    .update(updates)
    .eq('id', supplierId);

  if (error) throw error;
}
